import { useState, useEffect, useRef, useCallback } from "react";
import type { StateMap } from "../types";
import type { HiddenMap } from "../lib/hiddenFixes";
import { AuditSection } from "./AuditSection";
import { showAlert } from "../lib/dialog";
import { confirmDeletePopover } from "../lib/confirmPopover";
import { stripIgnoreControls } from "../lib/cardTrash";
import { loadAnthropicKey } from "./SettingsModal";
import { scheduleFlush } from "../lib/siteData";

interface Props {
  state: StateMap;
  siteBrand?: string;
  auditDate?: string;
  onApprove: (id: string, message?: string, isCreate?: boolean) => void;
  onIgnore: (id: string, checked: boolean) => void;
  onDone: (id: string, checked: boolean) => void;
  onReset: (id: string) => void;
  hidden: HiddenMap;
  onToggleHide: (id: string) => void;
  connected: boolean;
  fetchPageTags: (pathSlug: string) => Promise<string[] | null>;
  onOpenSchemaSetup?: () => void;
}

const normalizeUrl = (u: string): string => u.trim().toLowerCase().replace(/\/+$/, "");

// URLs that already have a card (read from each card's Visit-link href).
function extractCardedUrls(html: string): Set<string> {
  const set = new Set<string>();
  const re = /<a href="([^"]+)"[^>]*class="visit-link"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) set.add(normalizeUrl(m[1].replace(/&amp;/g, "&")));
  return set;
}
// Next free global card index so appended cards' field ids never collide.
function nextCardIndex(html: string): number {
  let max = -1;
  const re = /id="um-[^"]*-(\d+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) { const n = parseInt(m[1], 10); if (n > max) max = n; }
  return max + 1;
}

export function BulkMetaData(props: Props) {
  const [urls, setUrls] = useState(() => { try { return localStorage.getItem("lsg_bmd_urls") ?? ""; } catch { return ""; } });
  const [cardsHtml, setCardsHtml] = useState(() => { try { return localStorage.getItem("lsg_bmd_cards") ?? ""; } catch { return ""; } });
  const [building, setBuilding] = useState(false);
  const [status, setStatus] = useState("");
  const cardsRef = useRef<HTMLDivElement | null>(null);

  // Per-site (AUDIT_KEYS) — scheduleFlush so generate/delete/edit syncs to the server; without it a
  // refresh re-hydrates the server's stale copy and deleted cards come back.
  useEffect(() => { try { localStorage.setItem("lsg_bmd_urls", urls); scheduleFlush(); } catch { /* ignore */ } }, [urls]);
  useEffect(() => { try { localStorage.setItem("lsg_bmd_cards", cardsHtml); scheduleFlush(); } catch { /* ignore */ } }, [cardsHtml]);

  // Remove a single card (trash button) — drop it from the stored cards HTML.
  const removeCard = useCallback((cardId: string) => {
    setCardsHtml(prev => {
      const tmp = document.createElement("div");
      tmp.innerHTML = prev;
      tmp.querySelector(`[id="${cardId}"]`)?.remove();
      return tmp.innerHTML.trim();
    });
  }, []);

  // Wire the per-card trash buttons (same .cd-trash-btn as the Content Drawer cards).
  useEffect(() => {
    const root = cardsRef.current;
    if (!root) return;
    stripIgnoreControls(root); // Express Meta Data cards have no "Ignore" (incl. older cached cards)
    // Nor a "✓ Complete" / Done checkbox — strip it (and its adjacent separator), incl. older cached cards.
    root.querySelectorAll<HTMLElement>("label.ac-chk").forEach(lbl => {
      if (!/complete|done/i.test(lbl.textContent ?? "")) return;
      const prev = lbl.previousElementSibling;
      if (prev?.classList.contains("ac-sep")) prev.remove();
      else if (lbl.nextElementSibling?.classList.contains("ac-sep")) lbl.nextElementSibling.remove();
      lbl.remove();
    });
    // Drop the ⚡ from the "Approve Changes" button (kept via keepButtons, so AuditSection leaves it).
    root.querySelectorAll<HTMLButtonElement>(".ac-btn").forEach(btn => {
      if (/approve changes/i.test(btn.textContent ?? "")) btn.textContent = "Approve Changes";
    });
    // Drop the leading page icon (📄 / 🏠) from the card title badge.
    root.querySelectorAll<HTMLElement>(".um-title .b").forEach(b => {
      const t = b.textContent ?? "";
      const stripped = t.replace(/^\s*[\u{1F4C4}\u{1F3E0}]\s*/u, "");
      if (stripped !== t) b.textContent = stripped;
    });
    const cleanups: Array<() => void> = [];
    root.querySelectorAll<HTMLElement>(".cd-trash-btn").forEach(btn => {
      const id = btn.getAttribute("data-fix");
      if (!id) return;
      const h = (e: Event) => { e.preventDefault(); e.stopPropagation(); confirmDeletePopover(btn, () => removeCard(id)); };
      btn.addEventListener("click", h);
      cleanups.push(() => btn.removeEventListener("click", h));
    });
    return () => cleanups.forEach(fn => fn());
  }, [cardsHtml, removeCard]);

  // Expand/collapse: cards arrive collapsed (server adds .um-collapsed). Clicking a card's
  // title bar toggles it — but clicks on the buttons strip / inputs keep their own behavior.
  useEffect(() => {
    const root = cardsRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(".ac") || t.closest("input, textarea, button, label, a")) return; // let controls work
      const hd = t.closest(".um-hd");
      if (!hd) return;
      hd.closest(".um")?.classList.toggle("um-collapsed");
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [cardsHtml]);

  async function handleContinue() {
    const list = urls.split("\n").map(u => u.trim()).filter(Boolean);
    const valid = list.filter(u => /^https?:\/\//i.test(u));
    if (!valid.length) {
      void showAlert("Paste at least one full page URL (starting with https://) — one per line. A meta-update card is created for each.", { title: "No URLs" });
      return;
    }
    const carded = extractCardedUrls(cardsHtml);
    const seen = new Set<string>();
    const fresh = valid.filter(u => {
      const key = normalizeUrl(u);
      if (carded.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!fresh.length) {
      void showAlert("Every URL in the box already has a card. Add one or more new URLs.", { title: "Nothing new" });
      return;
    }
    const anthropicKey = loadAnthropicKey();
    if (!anthropicKey) {
      void showAlert("Anthropic API key not set.\n\nGo to ⚙ Settings and add your Anthropic API key (sk-ant-…).", { title: "API Key Required" });
      return;
    }

    setBuilding(true);
    setStatus(`Crawling ${fresh.length} page${fresh.length === 1 ? "" : "s"} and generating meta recommendations… (this can take 15–60 sec)`);
    try {
      const res = await fetch("/api/bulk-meta-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          urls: fresh,
          startIndex: nextCardIndex(cardsHtml),
          anthropicKey,
          brand: (localStorage.getItem("cover_business_name") ?? "").trim() || props.siteBrand || undefined,
          location: (localStorage.getItem("cover_location") ?? "").trim() || undefined,
        }),
      });
      const data = await res.json() as { ok: boolean; html?: string; error?: string };
      if (data.ok && data.html) {
        setCardsHtml(prev => (prev ? `${prev}\n${data.html}` : data.html!));
        setStatus("Done — click a card's title bar to expand it, review the recommended meta, and click ⚡ Approve Changes to apply.");
      } else {
        setStatus(`⚠ ${data.error ?? "Failed to generate meta cards"}`);
      }
    } catch (e) {
      setStatus(`⚠ ${e instanceof Error ? e.message : "Network error"}`);
    } finally {
      setBuilding(false);
    }
  }

  return (
    <div className="content-drawer" style={{ marginTop: 24 }}>
      <div className="cd-section">
        <div className="cd-section-hd">
          <span className="cd-hd-brand">Express Meta Data</span>
          <span className="audit-tip" style={{ marginLeft: 2 }}>
            <span className="audit-tip-icon">i</span>
            <span className="audit-tip-box" style={{ textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
              {"Paste the URLs of existing pages you want to optimize. On Continue, each page is crawled for its current H1, meta title and meta description, and an improved recommendation is generated — shown as a meta-update card identical to those in the Pages & Meta tab. Cards arrive minimized; click a card's title bar to expand it, then click ⚡ Approve Changes to push the meta to WordPress."}
            </span>
          </span>
        </div>
        <p className="cd-help">
          Paste one <strong>page URL per line</strong>, then click <strong>Continue</strong>. Each URL becomes a
          minimized meta-update card — click its title bar to expand, then <strong>⚡ Approve Changes</strong> to apply.
        </p>
        <textarea
          className="cd-titles"
          value={urls}
          onChange={e => setUrls(e.target.value)}
          rows={5}
          placeholder={"https://yoursite.com/services/teen-anxiety/\nhttps://yoursite.com/locations/montville/\nhttps://yoursite.com/about/"}
        />
        <div className="cd-actions">
          <button className="cd-continue-btn" onClick={handleContinue} disabled={building}>
            {building ? "Working…" : "Continue →"}
          </button>
          {status && <span className="cd-status">{status}</span>}
        </div>
      </div>

      {/* Generated meta-update cards — rendered through AuditSection (id="urls") so the
          ⚡ Approve Changes wiring → execPagesMetaFix (WordPress meta update) applies. */}
      {cardsHtml && (
        <div className="cd-cards express-meta-cards" ref={cardsRef}>
          <AuditSection
            id="urls"
            keepButtons
            html={cardsHtml}
            state={props.state}
            siteBrand={props.siteBrand}
            auditDate={props.auditDate}
            onApprove={props.onApprove}
            onIgnore={props.onIgnore}
            onDone={props.onDone}
            onReset={props.onReset}
            hidden={props.hidden}
            onToggleHide={props.onToggleHide}
            connected={props.connected}
            fetchPageTags={props.fetchPageTags}
            onOpenSchemaSetup={props.onOpenSchemaSetup}
          />
        </div>
      )}
    </div>
  );
}
